import React from 'react'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import './TablePager.css'

interface PaginationProps {
  page: number
  pageSize: number
  /** Historically callers pass totalPages (page count), not item count. */
  totalItems: number
  onChange: (page: number, pageSize: number) => void
  pageSizeOptions?: number[]
}

const DEFAULT_SIZES = [10, 25, 50]

const PaginationComp: React.FC<PaginationProps> = ({
  page,
  pageSize,
  totalItems,
  onChange,
  pageSizeOptions = DEFAULT_SIZES,
}) => {
  const { t } = useTranslation()
  const pageCount = Math.max(1, totalItems || 1)
  const sizes = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [pageSize, ...pageSizeOptions].sort((a, b) => a - b)

  if (pageCount <= 0) return null

  return (
    <div
      className="incyia-pager"
      role="navigation"
      aria-label={t('pagination', { defaultValue: 'Pagination' })}
      style={{ width: '100%', borderTop: 'none', padding: '4px 0' }}
    >
      <div className="incyia-pager-controls" style={{ marginLeft: 'auto' }}>
        <label className="incyia-pager-size">
          <span>{t('tablePager.rowsPerPage', { defaultValue: 'Rows' })}</span>
          <select
            value={pageSize}
            onChange={(e) => onChange(1, Number(e.target.value))}
            aria-label={t('tablePager.rowsPerPage', { defaultValue: 'Rows' })}
          >
            {sizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div className="incyia-pager-btns">
          <button
            type="button"
            className="incyia-pager-btn"
            disabled={page <= 1}
            onClick={() => onChange(page - 1, pageSize)}
            aria-label={t('tablePager.prev', { defaultValue: 'Previous page' })}
          >
            ‹
          </button>
          <span className="incyia-pager-page">
            {t('tablePager.pageOf', {
              page,
              pageCount,
              defaultValue: `${page} / ${pageCount}`,
            })}
          </span>
          <button
            type="button"
            className="incyia-pager-btn"
            disabled={page >= pageCount}
            onClick={() => onChange(page + 1, pageSize)}
            aria-label={t('tablePager.next', { defaultValue: 'Next page' })}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  )
}

export default PaginationComp
